import { Category, DetailedSize } from 'app/smart-replenishment/services/article-matrix';
import { DateInputStatus, Size, Type } from 'fc-ui-components';
import { SeasonName } from 'helpers';
import { Price } from 'services';
import { ArticleForOrder } from 'shared/services';
import { Sorting } from 'shared/services/product-search/searcher.types';
import { FormattedPrice } from 'src/app/b2b-order/helpers/price';
import { Dictionary } from 'types/dictionary';
import { Season } from '../helpers/season';
import { ArticleDate } from '../components/smart-replenishment/articles/types';

export type PauseStatus = { value: string; status: DateInputStatus };
export type StorePausedUntilAddon = { pausedUntil: PauseStatus };

export enum AutoRestockRhythm {
    Daily = 'daily',
    Weekly = 'weekly',
    TwiceAWeek = 'twice_a_week',
    TwiceAWeekMonWed = 'twice_a_week_mon_wed',
    Biweekly = 'biweekly'
}

export enum SalesApiHistoryReason {
    TargetStockApi = 'target_stock_api',
    TargetStockApiPlatformBulk = 'target_stock_api_platform_bulk',
    DynamicTargetStock = 'dynamic_target_stock'
}

export interface AutoRestockStore {
    gln: string;
    name?: string;
    isEnabled: boolean;
    rhythm: AutoRestockRhythm;
    minOrderQuantity: number;
    minOrderValue: string;
    pausedUntil: string;
    isDefault?: boolean;
    initialDelivery: InitialDelivery;
}

export enum AutoRestockStoresDropdownTreeGroupBy {
    ACTIVE = 'ACTIVE',
    IN_ACTIVE = 'IN_ACTIVE'
}

export type AutoRestockStoreItem = AutoRestockStore & StorePausedUntilAddon;

export type AutoRestockStoreWithPausedUntilAddon = Omit<AutoRestockStore, 'pausedUntil'> & StorePausedUntilAddon;

export type AutoRestockStoreWithCheckedAddon = AutoRestockStore & { checked: boolean };

export interface SuggestionBase {
    _id: string;
    brand: { _id: string; name: string };
    retailer: { _id: string; name: string };
    store: { gln: string; name?: string };
    updatedAt: string;
}

export interface Suggestion extends SuggestionBase {
    state: string;
    totalItems: number;
    totalPrice: number;
    articles: SuggestionArticle[];
}

export interface SuggestionArticle {
    products: SuggestionArticleProduct[];
    styleGroupId: string;
    colorGroupId: string;
    brandId: string;
    articleNumber: string;
    articleStockTurn: string;
    articleSellThrough: number;
    articleSell30Days: number;
    name: string;
    colorCode: string;
    colorName: string;
    fit?: string;
    images: SuggestionArticleImage[];
    imageUrl: string;
    categoryKey: Category;
    targetGroupKey: string;
    seasonKey: SeasonName;
    seasonYearKey: string;
    searchColorKey?: string[];
    catalogs: { _id: string; name: string }[];
    order?: {
        _id: number;
        orderNumber: string;
        orderedBy: string;
    };
}

export interface SuggestionArticleImage {
    productView: string;
    shootType: string;
    isMainImage: boolean;
    isPreliminary: boolean;
    _id: string;
    hash: string;
}

export interface SuggestionArticleProduct {
    gtin: string;
    suggestedQuantity: number;
    size?: string;
    retailerStock?: number;
    brandStock?: BrandStock;
    soldLast30Days: number | string;
    orderIncrement?: number;
    price: number;
}

export interface BrandStock {
    available: number;
    backorder?: {
        available: number;
        deliveryFrom: string;
        deliveryTo: string;
    };
}
export interface SuggestionsConfigurableSettingsFields {
    hasSuggestionsPerStore?: boolean;
}

export interface SuggestionsSettings extends Required<SuggestionsConfigurableSettingsFields> {
    brandId: string;
    retailerId: string;
}

export interface SuggestionStore {
    gln: string;
    name?: string;
    rhythm: string;
    minSuggestionQuantity: number;
    minSuggestionValue: number;
    minStockTurn: string;
    minSellThrough: number;
    teamMode: boolean;
    hasOpenSuggestions: boolean;
    suggestion?: {
        _id: string;
        state: SuggestionState;
    } | null;
}

export interface StoreSuggestionSettings {
    gln: string;
    rhythm: string;
    minSuggestionQuantity: number;
    minSuggestionValue: number;
    minStockTurn: string;
    minSellThrough: number;
    teamMode: boolean;
}

export type BrandRetailerSuggestionsSettings = Dictionary<Dictionary<SuggestionsSettings, 'retailerId'>, 'brandId'>;
export type BrandSuggestionsSettings = Dictionary<SuggestionsSettings, 'brandId'>;
export type RetailerSuggestionsSettings = Dictionary<SuggestionsSettings, 'retailerId'>;

export enum SuggestionRhythm {
    Never = 'never',
    Weekly = 'weekly',
    Biweekly = 'biweekly',
    WeeklyTuesday = 'weekly_tuesday'
}

export enum SuggestionsStoresGroupByEnum {
    Open = 'OPEN',
    NotOpen = 'NOT_OPEN'
}

export enum SuggestionsActionType {
    Accept = 'accept',
    Decline = 'decline',
    Add = 'add',
    Review = 'review'
}

export enum SuggestionState {
    Open = 'open',
    Draft = 'draft'
}

interface SmartReplenishmentAlert {
    _id: number;
    show: boolean;
    showClose: boolean;
    type: Type;
    additionalText: { show: boolean; value: string; action: () => void };
    size: Size;
    text: string;
}

export type SuggestionAlert = SmartReplenishmentAlert;

export type AutoRestockAlert = Omit<SmartReplenishmentAlert, '_id'>;

export interface ArticleSearchParameters {
    query?: string;
    offset: number;
    limit: number;
    sorting?: Sorting; // TODO: make me required when Add Article 2.0 is done
    period?: string; // TODO: make me required when Add Article 2.0 is done
    dateRange?: { from: string; to: string; label?: string };
    categories?: string[];
    seasons?: string[];
    targetGroups?: string[];
    colors?: string[];
    withRetailerStockOnly?: boolean; // TODO: make me required when done
    withBrandStockOnly?: boolean;
}

export interface AddArticleData {
    articles: ArticleForOrder[];
    total: number;
    isLoading: boolean;
}

export interface UserRequestServices {
    isARRequestSent: boolean;
    isSGRequestSent?: boolean;
    isISRequestSent?: boolean;
}

export enum RecipientAction {
    Create = 'create',
    Update = 'update',
    Delete = 'delete'
}

export interface Recipient {
    email: string;
    id: string;
    isDefault: boolean;
    enabled: boolean;
    action?: RecipientAction;
}

export interface AutoRestockProductMetrics {
    sell30Days: number;
    sellThrough: number;
    stockTurn: number;
}

export interface AutoRestockArticle {
    brandId: string;
    articleNumber: string;
    name: string;
    colorName: string;
    colorGroupId: string;
    colorCode: string;
    formattedColor: string;
    styleGroupId: string;
    images: AutoRestockImage[];
    imageUrl: string;
    sizes: AutoRestockProduct[];
    formattedPrice: FormattedPrice;
    seasonKey: SeasonName;
    seasonYearKey: string;
    targetGroupKey: string;
    categoryKey: string;
    searchColorKey?: string[];
    fit?: string;
    articleStockTurn: [number, number]; // Range format: [min, max] - same values for single store
    articleSellThrough: [number, number]; // Range format: [min, max] - same values for single store
    articleSell30Days: string;
    catalogs: { _id: string; name: string }[];
    metrics?: Dictionary<AutoRestockProductMetrics>;
    replenishmentSchedule?: Dictionary<
        { startDate: string; endDate: string; isActive: boolean; initialDelivery: InitialDeliveryState },
        'gln'
    >;
}

export interface AutoRestockImage {
    _id: string;
    isMainImage: boolean;
}

export interface SmartReplenishmentSearchArticle {
    _id: string;
    brandId: string;
    brandName: string;
    name: string;
    articleNumber: string;
    styleGroupId: string;
    colorGroupId: string;
    colorCode: string;
    colorName: string;
    stockTurn: number;
    sellThrough: number;
    soldQuantity: number;
    images: {
        _id: string;
        isMainImage: boolean;
    }[];
    gtins: {
        categoryKey?: Category;
        seasonKey?: SeasonName;
        seasonYearKey?: string;
        detailedSize?: DetailedSize;
        brandStock?: {
            available: number;
            backorder?: {
                available: number;
                deliveryFrom: string;
                deliveryTo: string;
            };
        };
        gtin: string;
        size: string;
        retailerStock: number;
        stockTurn: number;
        sellThrough: number;
        soldQuantity: number;
        targetQuantity?: number;
        orderIncrement?: number;
    }[];
    isStockAvailable: boolean;
    startDate?: ArticleDate;
    endDate?: ArticleDate;
}

export interface AutoRestockProduct {
    _id: string;
    gtin: string;
    brandId: string;
    styleGroupId: string;
    colorGroupId: string;
    colorCode: string;
    articleNumber: string;
    name: string;
    colorName: string;
    size: string;
    detailedSize?: DetailedSize;
    images: AutoRestockImage[];
    price: Price;
    seasonKey: SeasonName;
    seasonYearKey: string;
    targetGroupKey: string;
    categoryKey: Category;
    searchColorKey?: string[];
    articleStockTurn: [number, number]; // Range format: [min, max] - same values for single store
    articleSellThrough: [number, number]; // Range format: [min, max] - same values for single store
    articleSell30Days: number;
    orderIncrement?: number;
    catalogs: { _id: string; name: string }[];
    metrics?: Dictionary<AutoRestockProductMetrics>;
    autoRestock: Dictionary<
        {
            retailerStock: number;
            targetStock: number;
            startDate: string;
            endDate: string;
            soldLast30Days: number;
            isActive?: boolean;
            initialDelivery?: InitialDeliveryState;
            brandStock: {
                available: number;
                backorder?: {
                    available: number;
                    deliveryFrom: string;
                    deliveryTo: string;
                };
            };
        },
        'gln'
    >;
}

export interface AddArticleDataV2 {
    articles: SmartReplenishmentSearchArticle[];
    total: number;
    isLoading: boolean;
}

export interface AddArticleDetails {
    brandId: string;
    articleNumber: string;
    name: string;
    colorName: string;
    colorGroupId: string;
    colorCode: string;
    formattedColor: string;
    styleGroupId: string;
    images: AutoRestockImage[];
    imageUrl: string;
    sizes: AutoRestockProduct[];
    formattedPrice: FormattedPrice;
    seasonKey: SeasonName;
    seasonYearKey: string;
    targetGroupKey: string;
    categoryKey: string;
    articleStockTurn: [number, number]; // Range format: [min, max] - same values for single store
    articleSellThrough: [number, number]; // Range format: [min, max] - same values for single store
    articleSell30Days: string;
    isSelected?: boolean;
}

export interface Metrics {
    [storeGln: string]: {
        stockTurn: number;
        sellThrough: number;
        sell30Days: number;
    };
}

export enum AutoRestockNavigationState {
    BrandPage = 'AUTO_RESTOCK_BRAND_PAGE',
    ManufacturerPage = 'AUTO_RESTOCK_MANUFACTURER_PAGE'
}

export interface InitialDelivery {
    enabled: boolean;
    seasons: Season[];
}

export interface InitialDeliveryState {
    status: InitialDeliveryStatus;
    receivedCount?: number;
    totalCount?: number;
    awaitingSizes?: string[];
}

export enum InitialDeliveryStatus {
    Enabled = 'enabled',
    Disabled = 'disabled',
    Delivered = 'delivered',
    PartiallyDelivered = 'partially_delivered'
}

export enum LoadingState {
    Fetching = 'fetching',
    Refreshing = 'refreshing',
    Idle = 'idle'
}
